// ===========================================
// Make-up Booking Tab
// ===========================================
// Tab content for booking make-up lessons using credits

import { useState, useMemo, useEffect } from 'react';
import { format, isSameDay, parseISO } from 'date-fns';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  CircularProgress,
  LinearProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Event as EventIcon,
  Cancel as CancelIcon,
  MusicNote as InstrumentIcon,
  Person as PersonIcon,
  CalendarMonth as CalendarIcon,
} from '@mui/icons-material';
import {
  useMakeupCredits,
  useMakeupEligibilities,
  useMakeupSlots,
  useMakeupHistory,
  useBookMakeupSlot,
  useCancelMakeupBooking,
} from '../../hooks/useBooking';
import { useStudentEnrollments } from '../../hooks/useLessons';
import {
  MakeupEligibility,
  MakeupSlot,
} from '../../api/booking.api';
import { formatUTCDate, localDateFromKey } from '../../utils/dateUtils';

/** A calendar day as dd/mm/yyyy (Critical Rule 7), read with UTC getters (`@db.Date` values). */
const dayAu = (value: string): string =>
  formatUTCDate(value, { day: '2-digit', month: '2-digit', year: 'numeric' });

/**
 * What an offer says about when it can be used (#462). The SERVER decides `expired` and
 * `validUntil`; this only words them. An offer that cannot be placed in a term says nothing.
 */
function offerWindowLabel(elig: MakeupEligibility): { text: string; expired: boolean } | null {
  if (!elig.creditTerm) return null;
  if (elig.expired) {
    return {
      text: `${elig.creditTerm.name}: expired${elig.validUntil ? ` ${dayAu(elig.validUntil)}` : ''}`,
      expired: true,
    };
  }
  return {
    text: `${elig.creditTerm.name}${elig.validUntil ? `: use by ${dayAu(elig.validUntil)}` : ''}`,
    expired: false,
  };
}

// ===========================================
// TYPES
// ===========================================

interface BookingModalState {
  open: boolean;
  eligibility: MakeupEligibility | null;
}

interface CancelModalState {
  open: boolean;
  slot: MakeupSlot | null;
}

// ===========================================
// COMPONENT PROPS
// ===========================================

interface MakeupBookingTabProps {
  hideBookingsList?: boolean;
}

// ===========================================
// COMPONENT
// ===========================================

export default function MakeupBookingTab({ hideBookingsList = false }: MakeupBookingTabProps) {
  // State
  const [bookingModal, setBookingModal] = useState<BookingModalState>({
    open: false,
    eligibility: null,
  });
  const [cancelModal, setCancelModal] = useState<CancelModalState>({
    open: false,
    slot: null,
  });
  const [selectedSlot, setSelectedSlot] = useState<MakeupSlot | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  // For admin credits: selected instrument entry (includes teacherId)
  const [selectedEnrollmentOption, setSelectedEnrollmentOption] = useState<{
    instrumentId: string;
    instrumentName: string;
    teacherId: string | null;
    teacherName: string | null;
  } | null>(null);

  // Queries
  const { data: credits, isLoading: creditsLoading } = useMakeupCredits();
  const { data: eligibilities, isLoading: eligibilitiesLoading } = useMakeupEligibilities();
  const { data: history, isLoading: historyLoading } = useMakeupHistory();

  // For admin credits: fetch the student's enrolled lessons to derive teacher/instrument
  const isAdminCredit = bookingModal.open && bookingModal.eligibility?.adminOffered && !bookingModal.eligibility?.Lesson;
  const { data: studentEnrollments, isLoading: enrollmentsLoading } = useStudentEnrollments(
    isAdminCredit ? bookingModal.eligibility?.studentId ?? null : null
  );

  // For absence-based: derive instrument and teacher from the missed lesson
  // For admin credits: derive from selected enrollment option
  const activeInstrumentId = bookingModal.eligibility?.instrumentId
    || selectedEnrollmentOption?.instrumentId
    || '';
  const activeTeacherId = bookingModal.eligibility?.Lesson?.Instructor?.id
    || selectedEnrollmentOption?.teacherId
    || undefined;
  // With the offer, the SERVER offers only the slots its credit window allows (#462).
  const { data: availableSlots, isLoading: slotsLoading } = useMakeupSlots(
    activeInstrumentId,
    undefined,
    activeTeacherId,
    bookingModal.eligibility?.id
  );

  // The offer's own term has finished: parents book inside the term only, so say that instead of
  // the generic "check back later" (the slots will not come back).
  const creditTermEnded = (() => {
    const t = bookingModal.eligibility?.creditTerm;
    if (!t) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return localDateFromKey(t.endDate) < today;
  })();

  // Mutations
  const bookSlot = useBookMakeupSlot();
  const cancelBooking = useCancelMakeupBooking();

  // Auto-select the enrollment option when there's exactly one (most common case)
  useEffect(() => {
    if (!isAdminCredit || !studentEnrollments) return;
    if (selectedEnrollmentOption) return; // already selected
    if (studentEnrollments.instruments.length === 1) {
      const inst = studentEnrollments.instruments[0];
      setSelectedEnrollmentOption({
        instrumentId: inst.id,
        instrumentName: inst.name,
        teacherId: inst.teacherId,
        teacherName: inst.teacherName,
      });
    }
  }, [isAdminCredit, studentEnrollments, selectedEnrollmentOption]);

  // Group eligibilities by student
  const eligibilitiesByStudent = useMemo(() => {
    const grouped: Record<string, MakeupEligibility[]> = {};
    (eligibilities ?? []).forEach((elig) => {
      if (!grouped[elig.studentId]) {
        grouped[elig.studentId] = [];
      }
      grouped[elig.studentId].push(elig);
    });
    return grouped;
  }, [eligibilities]);

  // Get upcoming booked slots from history
  const upcomingBookings = useMemo(() => {
    const now = new Date();
    return (history ?? [])
      .filter((slot) => slot.isBooked && new Date(slot.date) >= now)
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [history]);

  // Past bookings
  const pastBookings = useMemo(() => {
    const now = new Date();
    return (history ?? [])
      .filter((slot) => slot.isBooked && new Date(slot.date) < now)
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .slice(0, 5);
  }, [history]);

  // Group available slots by date (show next 16 days only)
  const availableDates = useMemo(() => {
    if (!availableSlots) return [];
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    const uniqueDates = new Set<string>();
    availableSlots.forEach((slot) => {
      uniqueDates.add(slot.date);
    });

    const allDates = Array.from(uniqueDates)
      .map((dateStr) => parseISO(dateStr))
      .filter((date) => date >= now) // Only future dates
      .sort((a, b) => a.getTime() - b.getTime());

    // Limit to next 16 days
    return allDates.slice(0, 16);
  }, [availableSlots]);

  // Get slots for selected date
  const slotsForSelectedDate = useMemo(() => {
    if (!selectedDate || !availableSlots) return [];
    return availableSlots
      .filter((slot) => isSameDay(parseISO(slot.date), selectedDate))
      .sort((a, b) => a.startTime.localeCompare(b.startTime));
  }, [selectedDate, availableSlots]);

  // Handlers
  const handleOpenBookingModal = (eligibility: MakeupEligibility) => {
    setBookingModal({ open: true, eligibility });
    setSelectedSlot(null);
    setSelectedDate(null);
  };

  const handleCloseBookingModal = () => {
    setBookingModal({ open: false, eligibility: null });
    setSelectedSlot(null);
    setSelectedDate(null);
    setSelectedEnrollmentOption(null);
  };

  const handleConfirmBooking = () => {
    if (!bookingModal.eligibility || !selectedSlot) return;

    bookSlot.mutate(
      {
        slotId: selectedSlot.id,
        studentId: bookingModal.eligibility.studentId,
        eligibilityId: bookingModal.eligibility.id,
        // Include slot details for virtual slots
        teacherId: selectedSlot.teacherId,
        termId: selectedSlot.termId,
        instrumentId: selectedSlot.instrumentId,
        roomId: selectedSlot.roomId || undefined,
        date: selectedSlot.date,
        startTime: selectedSlot.startTime,
        endTime: selectedSlot.endTime,
      },
      {
        onSuccess: () => {
          handleCloseBookingModal();
        },
      }
    );
  };

  const handleOpenCancelModal = (slot: MakeupSlot) => {
    setCancelModal({ open: true, slot });
  };

  const handleCloseCancelModal = () => {
    setCancelModal({ open: false, slot: null });
  };

  const handleConfirmCancel = () => {
    if (!cancelModal.slot) return;

    cancelBooking.mutate(cancelModal.slot.id, {
      onSuccess: () => {
        handleCloseCancelModal();
      },
    });
  };

  // Loading state
  const isLoading = creditsLoading || eligibilitiesLoading || historyLoading;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const hasEligibilities = eligibilities && eligibilities.length > 0;

  return (
    <Box>
      {/* Credits Summary */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Make-up Credits
          </Typography>

          {!credits || credits.length === 0 ? (
            <Alert severity="info">
              No make-up credits available. Credits are granted by the admin when your child has an
              excused absence.
            </Alert>
          ) : (
            <Grid container spacing={2}>
              {credits.map((credit) => (
                <Grid item xs={12} sm={6} md={4} key={`${credit.studentId}-${credit.termId}`}>
                  <Paper
                    variant="outlined"
                    sx={{ p: 2, textAlign: 'center' }}
                  >
                    <Typography variant="subtitle1" gutterBottom>
                      {credit.studentName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {credit.termName}
                    </Typography>
                    <Box sx={{ my: 2 }}>
                      <Typography variant="h4" color="primary">
                        {credit.creditsRemaining}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        credits remaining
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={(credit.creditsUsed / credit.creditsGranted) * 100}
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {credit.creditsUsed} of {credit.creditsGranted} used
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          )}
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {/* Eligibilities Section */}
        <Grid item xs={12} md={hideBookingsList ? 12 : 7}>
          <Typography variant="h6" gutterBottom>
            Available Make-up Eligibilities
          </Typography>

          {!hasEligibilities ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              No make-up eligibilities available. When your child has an excused absence, the admin
              will grant a make-up eligibility that you can redeem here.
            </Alert>
          ) : (
            Object.entries(eligibilitiesByStudent).map(([studentId, studentEligibilities]) => {
              const student = studentEligibilities[0]?.Student;
              if (!student) return null;

              return (
                <Card key={studentId} sx={{ mb: 2 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <PersonIcon color="primary" />
                      <Typography variant="subtitle1">
                        {student.firstName} {student.lastName}
                      </Typography>
                      <Chip
                        label={`${studentEligibilities.length} available`}
                        size="small"
                        color="primary"
                      />
                    </Box>

                    <List dense>
                      {studentEligibilities.map((elig) => {
                        const isAdminOffered = elig.adminOffered || (!elig.Attendance && !elig.Lesson);

                        return (
                          <Paper key={elig.id} variant="outlined" sx={{ mb: 1 }}>
                            <ListItem>
                              <ListItemText
                                primary={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <InstrumentIcon fontSize="small" />
                                    <Typography variant="body2">
                                      {elig.Instrument?.name ?? 'Any instrument'}
                                    </Typography>
                                    {isAdminOffered && (
                                      <Chip label="From the school" size="small" color="secondary" />
                                    )}
                                  </Box>
                                }
                                secondary={
                                  <>
                                    {isAdminOffered ? (
                                      <Typography variant="caption" color="text.secondary" display="block">
                                        Make-up credit sent by the school
                                      </Typography>
                                    ) : (
                                      <>
                                        <Typography variant="caption" display="block">
                                          Missed: {elig.Lesson!.name} on {dayAu(elig.Attendance!.date)}
                                          {elig.Lesson!.Instructor && (
                                            <> with {elig.Lesson!.Instructor.User.firstName} {elig.Lesson!.Instructor.User.lastName}</>
                                          )}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                          Reason: {elig.Attendance!.absenceReason || 'Not specified'}
                                        </Typography>
                                      </>
                                    )}
                                    {(() => {
                                      const windowLabel = offerWindowLabel(elig);
                                      return windowLabel ? (
                                        <Typography
                                          variant="caption"
                                          display="block"
                                          color={windowLabel.expired ? 'error' : 'text.secondary'}
                                        >
                                          {windowLabel.text}
                                        </Typography>
                                      ) : null;
                                    })()}
                                  </>
                                }
                              />
                              <ListItemSecondaryAction>
                                <Button
                                  size="small"
                                  variant="contained"
                                  startIcon={<EventIcon />}
                                  onClick={() => handleOpenBookingModal(elig)}
                                  disabled={elig.expired}
                                >
                                  {elig.expired ? 'Expired' : 'Book'}
                                </Button>
                              </ListItemSecondaryAction>
                            </ListItem>
                          </Paper>
                        );
                      })}
                    </List>
                  </CardContent>
                </Card>
              );
            })
          )}
        </Grid>

        {/* Bookings History - only show if not hidden */}
        {!hideBookingsList && (
          <Grid item xs={12} md={5}>
            <Typography variant="h6" gutterBottom>
              Upcoming Make-up Lessons
            </Typography>

            {upcomingBookings.length === 0 ? (
              <Alert severity="info" sx={{ mb: 3 }}>
                No upcoming make-up lessons booked.
              </Alert>
            ) : (
              <List sx={{ mb: 3 }}>
                {upcomingBookings.map((slot) => (
                  <Paper key={slot.id} sx={{ mb: 1 }}>
                    <ListItem>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CalendarIcon fontSize="small" color="primary" />
                            <Typography variant="body2">
                              {format(new Date(slot.date), 'EEE, d MMM yyyy')}
                            </Typography>
                            <Chip
                              label={`${slot.startTime} - ${slot.endTime}`}
                              size="small"
                              variant="outlined"
                            />
                          </Box>
                        }
                        secondary={
                          <>
                            <Typography variant="caption" display="block">
                              {slot.Instrument?.name} with {slot.Teacher?.User?.firstName}{' '}
                              {slot.Teacher?.User?.lastName}
                            </Typography>
                            {slot.Room && (
                              <Typography variant="caption" color="text.secondary">
                                {slot.Room.Location?.name} - {slot.Room.name}
                              </Typography>
                            )}
                          </>
                        }
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleOpenCancelModal(slot)}
                          title="Cancel booking"
                        >
                          <CancelIcon fontSize="small" />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  </Paper>
                ))}
              </List>
            )}

            {pastBookings.length > 0 && (
              <>
                <Typography variant="h6" gutterBottom>
                  Recent Make-up Lessons
                </Typography>
                <List>
                  {pastBookings.map((slot) => (
                    <Paper key={slot.id} sx={{ mb: 1, opacity: 0.7 }}>
                      <ListItem>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CalendarIcon fontSize="small" color="disabled" />
                              <Typography variant="body2" color="text.secondary">
                                {format(new Date(slot.date), 'EEE, d MMM yyyy')}
                              </Typography>
                              <Chip
                                label="Completed"
                                size="small"
                                color="success"
                              />
                            </Box>
                          }
                          secondary={
                            <Typography variant="caption" color="text.secondary">
                              {slot.Instrument?.name} with {slot.Teacher?.User?.firstName}{' '}
                              {slot.Teacher?.User?.lastName}
                            </Typography>
                          }
                        />
                      </ListItem>
                    </Paper>
                  ))}
                </List>
              </>
            )}
          </Grid>
        )}
      </Grid>

      {/* Booking Modal */}
      <Dialog
        open={bookingModal.open}
        onClose={handleCloseBookingModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Book Make-up Lesson</DialogTitle>
        <DialogContent>
          {bookingModal.eligibility && (
            <>
              {/* Absence-based: show missed lesson details */}
              {bookingModal.eligibility.Lesson ? (
                <Box sx={{ mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    Missed Lesson
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {bookingModal.eligibility.Lesson.name}
                  </Typography>
                  {bookingModal.eligibility.Lesson.Instructor && (
                    <Typography variant="body2" color="text.secondary">
                      Teacher: {bookingModal.eligibility.Lesson.Instructor.User.firstName}{' '}
                      {bookingModal.eligibility.Lesson.Instructor.User.lastName}
                    </Typography>
                  )}
                  {bookingModal.eligibility.Instrument && (
                    <Typography variant="body2" color="text.secondary">
                      Instrument: {bookingModal.eligibility.Instrument.name}
                    </Typography>
                  )}
                  {bookingModal.eligibility.Attendance && (
                    <Typography variant="body2" color="text.secondary">
                      Absent: {format(new Date(bookingModal.eligibility.Attendance.date), 'd MMMM yyyy')}
                      {bookingModal.eligibility.Attendance.absenceReason && (
                        <> &mdash; {bookingModal.eligibility.Attendance.absenceReason}</>
                      )}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: 'block' }}>
                    Available slots are shown for this teacher and instrument.
                  </Typography>
                </Box>
              ) : (
                /* Admin-offered: derive teacher/instrument from student's enrolled lessons */
                <Box sx={{ mb: 2 }}>
                  <Chip label="From the school" size="small" color="info" sx={{ mb: 1.5 }} />
                  {enrollmentsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                      <CircularProgress size={20} />
                    </Box>
                  ) : studentEnrollments && studentEnrollments.instruments.length > 1 ? (
                    /* Multiple enrolled instruments: show selector with teacher name */
                    <FormControl fullWidth size="small">
                      <InputLabel>Select Lesson</InputLabel>
                      <Select
                        value={selectedEnrollmentOption?.instrumentId ?? ''}
                        label="Select Lesson"
                        onChange={(e) => {
                          const inst = studentEnrollments.instruments.find((i) => i.id === e.target.value);
                          if (inst) {
                            setSelectedEnrollmentOption({
                              instrumentId: inst.id,
                              instrumentName: inst.name,
                              teacherId: inst.teacherId,
                              teacherName: inst.teacherName,
                            });
                            setSelectedDate(null);
                            setSelectedSlot(null);
                          }
                        }}
                      >
                        {studentEnrollments.instruments.map((inst) => (
                          <MenuItem key={inst.id} value={inst.id}>
                            {inst.name}{inst.teacherName ? ` — ${inst.teacherName}` : ''}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : selectedEnrollmentOption ? (
                    /* Single instrument auto-selected: show as info box */
                    <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                        Lesson Details
                      </Typography>
                      {selectedEnrollmentOption.teacherName && (
                        <Typography variant="body2" color="text.secondary">
                          Teacher: {selectedEnrollmentOption.teacherName}
                        </Typography>
                      )}
                      <Typography variant="body2" color="text.secondary">
                        Instrument: {selectedEnrollmentOption.instrumentName}
                      </Typography>
                      <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: 'block' }}>
                        Available slots are shown for this teacher and instrument.
                      </Typography>
                    </Box>
                  ) : (
                    <Alert severity="info">No enrolled lessons found for this student.</Alert>
                  )}
                </Box>
              )}
            </>
          )}

          <Divider sx={{ my: 2 }} />

          {!activeInstrumentId ? (
            <Alert severity="info">
              {isAdminCredit && enrollmentsLoading
                ? 'Loading lesson details…'
                : isAdminCredit && studentEnrollments && studentEnrollments.instruments.length > 1
                ? 'Please select a lesson above to see available slots.'
                : 'Please select a lesson above to see available slots.'}
            </Alert>
          ) : slotsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : !availableSlots || availableSlots.length === 0 ? (
            <Alert severity="warning">
              {creditTermEnded && bookingModal.eligibility?.creditTerm
                ? `Make-up slots can only be booked during ${bookingModal.eligibility.creditTerm.name}, which has finished. Please contact the school to arrange this make-up.`
                : 'No available makeup slots for this instrument at this time. Please check back later or contact the school to request a makeup lesson slot.'}
            </Alert>
          ) : (
            <>
              {/* Step 1: Select Date */}
              <Typography variant="subtitle2" gutterBottom>
                Step 1: Select a Date
              </Typography>
              <Grid container spacing={0.5} sx={{ mb: 3 }}>
                {availableDates.map((date) => (
                  <Grid item xs={3} sm={2} md={1.5} key={date.toISOString()}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 0.5,
                        cursor: 'pointer',
                        textAlign: 'center',
                        bgcolor: selectedDate && isSameDay(date, selectedDate) ? 'primary.light' : 'background.paper',
                        borderColor: selectedDate && isSameDay(date, selectedDate) ? 'primary.main' : 'divider',
                        borderWidth: selectedDate && isSameDay(date, selectedDate) ? 2 : 1,
                        '&:hover': {
                          bgcolor: selectedDate && isSameDay(date, selectedDate) ? 'primary.light' : 'action.hover',
                        },
                      }}
                      onClick={() => {
                        setSelectedDate(date);
                        setSelectedSlot(null);
                      }}
                    >
                      <Typography variant="caption" fontSize="0.65rem" color="text.secondary" display="block">
                        {format(date, 'EEE')}
                      </Typography>
                      <Typography variant="body1" fontWeight="medium" color={selectedDate && isSameDay(date, selectedDate) ? 'primary.main' : 'text.primary'}>
                        {format(date, 'd')}
                      </Typography>
                      <Typography variant="caption" fontSize="0.65rem" color="text.secondary">
                        {format(date, 'MMM')}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>

              {/* Step 2: Select Time */}
              {selectedDate && (
                <>
                  <Typography variant="subtitle2" gutterBottom>
                    Step 2: Select a Time Slot for {format(selectedDate, 'EEEE, d MMMM yyyy')}
                  </Typography>
                  {slotsForSelectedDate.length === 0 ? (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      No time slots available for this date.
                    </Alert>
                  ) : (
                    <Grid container spacing={1}>
                      {slotsForSelectedDate.map((slot) => (
                        <Grid item xs={12} sm={6} md={4} key={slot.id}>
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 1.5,
                              cursor: 'pointer',
                              height: '100%',
                              bgcolor: selectedSlot?.id === slot.id ? 'primary.light' : 'background.paper',
                              borderColor: selectedSlot?.id === slot.id ? 'primary.main' : 'divider',
                              borderWidth: selectedSlot?.id === slot.id ? 2 : 1,
                              '&:hover': {
                                bgcolor: selectedSlot?.id === slot.id ? 'primary.light' : 'action.hover',
                              },
                            }}
                            onClick={() => setSelectedSlot(slot)}
                          >
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2" fontWeight="medium">
                                  {slot.startTime} - {slot.endTime}
                                </Typography>
                                {selectedSlot?.id === slot.id && (
                                  <Chip label="✓" color="primary" size="small" sx={{ height: 20, minWidth: 20, '& .MuiChip-label': { px: 0.5 } }} />
                                )}
                              </Box>
                              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>
                                {slot.Teacher?.User?.firstName} {slot.Teacher?.User?.lastName}
                              </Typography>
                              {slot.Room && (
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}>
                                  {slot.Room.Location?.name} - {slot.Room.name}
                                </Typography>
                              )}
                            </Box>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBookingModal}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleConfirmBooking}
            disabled={!selectedSlot || bookSlot.isPending}
          >
            {bookSlot.isPending ? <CircularProgress size={20} /> : 'Confirm Booking'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={cancelModal.open} onClose={handleCloseCancelModal}>
        <DialogTitle>Cancel Make-up Booking</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Are you sure you want to cancel this make-up lesson?
          </Typography>
          {cancelModal.slot && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {format(new Date(cancelModal.slot.date), 'EEEE, d MMMM yyyy')}
                <br />
                {cancelModal.slot.startTime} - {cancelModal.slot.endTime}
                <br />
                {cancelModal.slot.Instrument?.name} with {cancelModal.slot.Teacher?.User?.firstName}{' '}
                {cancelModal.slot.Teacher?.User?.lastName}
              </Typography>
            </Box>
          )}
          <Alert severity="info" sx={{ mt: 2 }}>
            Your make-up eligibility will be restored and you can book a different slot.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCancelModal}>Keep Booking</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmCancel}
            disabled={cancelBooking.isPending}
          >
            {cancelBooking.isPending ? <CircularProgress size={20} /> : 'Cancel Booking'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
